const {KitModule, registerModule} = window;

// no space as it will be triggered from click
const OPEN_SUBNAV_KEYS = ['Enter', 'ArrowDown'];
const NAV_THEMES = ['light', 'sand', 'blue', 'dark'];
const PAGE_THEME_TOKEN = 'page';
const NAV_THEME_VAR_GROUPS = {
    background: ['--nav-theme-bg', '--nav-theme-subnav-bg', '--nav-theme-secondary-btn-color'],
    text: [
        '--nav-theme-color',
        '--nav-theme-subnav-color',
        '--nav-theme-subnav-subtitle-color',
        '--nav-theme-primary-text-color',
        '--nav-theme-secondary-btn-bg',
        '--nav-theme-secondary-btn-hover-bg',
    ],
    divider: ['--nav-theme-border', '--nav-theme-subnav-border'],
};

class Nav extends KitModule {
    constructor($element) {
        super($element);

        this.setVars();
        this.bindEvents();
    }

    setVars() {
        this.searchIsOpen = false;
        this.isMenuOpen = false;
        this.isClicking = false;
        this.clickOpenedSubnav = null;
        this.$openSubnav = null;

        this.$nav = this.$element;
        this.$navContainer = this.$element.querySelector('[data-js-container]');
        this.navScrollThreshold = 0;

        // Track sections for scroll-based nav theming
        this.$sections = document.querySelectorAll('.section, [data-section-type], [data-nav-theme]');

        this.onSubnavMouseLeave = this.onSubnavMouseLeave.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onDocumentClickBound = this.onDocumentClick.bind(this);
    }

    bindEvents() {
        this.$element.addEventListener('keydown', this);
        this.$element.addEventListener('click', this);
        this.$element.addEventListener('mouseover', this);
        this.$menuLabel.addEventListener('click', this.onMenuLabelClick.bind(this));
        this.$menuLabel.addEventListener('touchstart', function() {});

        document.addEventListener('click', this.onDocumentClickBound);
        window.addEventListener('searchopen', this);
        window.addEventListener('searchclose', this);

        this.bindMediaQuery();
        this.bindScroll();
    }

    bindMediaQuery() {
        const rootStyle = getComputedStyle(document.documentElement);

        // max nav - mobile
        const maxQuery = rootStyle.getPropertyValue('--max-nav-media-query');
        this.maxMediaQuery = window.matchMedia(maxQuery);
        // listen and set initial
        this.maxMediaQuery.addEventListener('change', this.onMaxMediaChange.bind(this));
        this.onMaxMediaChange(this.maxMediaQuery);

        // min nav - desktop
        const minQuery = rootStyle.getPropertyValue('--min-nav-media-query');
        this.minMediaQuery = window.matchMedia(minQuery);
        // listen and set initial
        this.minMediaQuery.addEventListener('change', this.onMinMediaChange.bind(this));
        this.onMinMediaChange(this.minMediaQuery);
    }

    bindScroll() {
        if (!this.$nav) return;
        if (!document.documentElement.classList.contains('js')) return;

        window.addEventListener('scroll', this.onScroll, {passive: true});

        // Set initial state based on current scroll
        this.onScroll();
    }

    updateNavThemeByScroll() {
        if (!this.$nav) return;
        if (!this.$sections || !this.$sections.length) {
            this.clearCustomNavTheme();
            this.applyNavThemeClass(this.resolveBodyTheme());
            return;
        }

        const navLine = 30;

        let activeSection = null;

        this.$sections.forEach((section) => {
            const rect = section.getBoundingClientRect();

            // Section that currently spans the nav line
            if (rect.top <= navLine && rect.bottom >= navLine) {
                activeSection = section;
            }
        });

        if (!activeSection) {
            this.clearCustomNavTheme();
            this.applyNavThemeClass(this.resolveBodyTheme());
            return;
        }

        this.applyCustomNavThemeBySection(activeSection);
        const theme = this.resolveThemeForSection(activeSection);
        this.applyNavThemeClass(theme);
    }

    resolveThemeForSection(section) {
        if (!section) return null;

        const explicitTheme = section.getAttribute('data-nav-theme');
        if (explicitTheme === PAGE_THEME_TOKEN) {
            return this.resolveBodyTheme();
        }
        if (NAV_THEMES.includes(explicitTheme)) {
            return explicitTheme;
        }

        for (const theme of NAV_THEMES) {
            if (section.classList.contains(`theme-${theme}`)) {
                return theme;
            }
        }

        const closestThemedAncestor = section.closest('.theme-light, .theme-sand, .theme-blue, .theme-dark');
        if (!closestThemedAncestor) return null;

        for (const theme of NAV_THEMES) {
            if (closestThemedAncestor.classList.contains(`theme-${theme}`)) {
                return theme;
            }
        }

        return null;
    }

    applyCustomNavThemeBySection(section) {
        if (!this.$nav || !section) return;
        const hasCustomOverrides =
            section.hasAttribute('data-nav-bg') ||
            section.hasAttribute('data-nav-text') ||
            section.hasAttribute('data-nav-divider');
        if (!hasCustomOverrides) {
            this.clearCustomNavTheme();
            return;
        }

        const background = this.normalizeHex(section.getAttribute('data-nav-bg'));
        const text = this.normalizeHex(section.getAttribute('data-nav-text'));
        const divider = section.getAttribute('data-nav-divider');

        this.setOrClearNavThemeVars(NAV_THEME_VAR_GROUPS.background, background);
        this.setOrClearNavThemeVars(NAV_THEME_VAR_GROUPS.text, text);
        this.setOrClearNavThemeVars(NAV_THEME_VAR_GROUPS.divider, divider);
    }

    clearCustomNavTheme() {
        if (!this.$nav) return;
        Object.values(NAV_THEME_VAR_GROUPS).flat().forEach((name) => this.$nav.style.removeProperty(name));
    }

    setOrClearNavThemeVars(vars, value) {
        if (!this.$nav || !Array.isArray(vars) || !vars.length) return;
        vars.forEach((name) => {
            if (value) {
                this.$nav.style.setProperty(name, value);
            } else {
                this.$nav.style.removeProperty(name);
            }
        });
    }

    normalizeHex(value) {
        const raw = typeof value === 'string' ? value.trim() : '';
        if (!raw) return null;
        const prefixed = raw.startsWith('#') ? raw : `#${raw}`;
        return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(prefixed) ? prefixed : null;
    }

    resolveBodyTheme() {
        for (const theme of NAV_THEMES) {
            if (document.body.classList.contains(`theme-${theme}`)) {
                return theme;
            }
        }

        return null;
    }

    applyNavThemeClass(theme) {
        if (!this.$nav) return;

        NAV_THEMES.forEach((themeName) => {
            this.$nav.classList.toggle(`nav--over-${themeName}`, themeName === theme);
        });
    }

    onScroll() {
        if (!this.$nav) return;

        const scrolled = window.scrollY > this.navScrollThreshold;

        if (scrolled) {
            this.$nav.classList.add('nav--scrolled');
            this.$nav.classList.remove('nav--top');
        } else {
            this.$nav.classList.remove('nav--scrolled');
            this.$nav.classList.add('nav--top');
        }

        // Update nav theme based on which section is currently behind the nav
        this.updateNavThemeByScroll();
    }

    onMaxMediaChange(mobileMediaQuery) {
    // mobile
        if (mobileMediaQuery.matches) {
            this.isMobile = true;
            this.isDesktop = false;
        }

        document.documentElement.classList.remove('nav-menu-open');
    }

    onMinMediaChange(desktopMediaQuery) {
    // desktop
        if (desktopMediaQuery.matches) {
            this.isDesktop = true;
            this.isMobile = false;
        }

        document.documentElement.classList.remove('nav-menu-open');
    }

    onsearchopen() {
        this.searchIsOpen = true;
    }

    onsearchclose() {
        this.searchIsOpen = false;
        if (this.isMobile) {
            this.closeSubnav();
        }
    }

    // toggle subnav on .nav__item__button click
    onclick(event) {
        const $itemButton = event.target.closest('.nav__item__button');
        const $nestedButton = event.target.closest('[data-js-nested-button]');

        // Handle nested dropdown buttons
        if ($nestedButton) {
            this.handleNestedDropdown($nestedButton);
            return;
        }

        if (!$itemButton) return;

        const $item = $itemButton.closest('.nav__item');

        // Prevent hover from interfering with click
        this.isClicking = true;
        setTimeout(() => {
            this.isClicking = false;
        }, 100);

        // Handle click behavior separately from hover
        if ($item === this.clickOpenedSubnav) {
            // Clicking the same button that was clicked before - close it
            this.closeSubnav();
            this.clickOpenedSubnav = null;
        } else {
            // Clicking a different button or no button was clicked before - open it
            this.closeSubnav(); // Close any existing subnav
            this.clickOpenedSubnav = null; // Reset click state
            this.openSubnav($item);
            this.clickOpenedSubnav = $item;
        }
    }

    onkeydown(event) {
        if (event.key === 'Escape') {
            this.closeSubnav();
            return;
        }
        const isOpeningSubnav = OPEN_SUBNAV_KEYS.includes(event.key) &&
      event.target.matches('[data-js-level-1-button]');

        if (isOpeningSubnav) {
            event.preventDefault();
            const $item = event.target.closest('.nav__item');
            if ($item === this.$openSubnavItem && event.key !== 'ArrowDown') {
                this.closeSubnav();
            } else {
                this.openSubnav($item);
            }
        }
    }

    // open/close subnav on hover, desktop only
    onmouseover(event) {
        // desktop-only hover behavior
        if (!this.isDesktop || this.isClicking) return;

        // Check if hovering over any nav item (button or link)
        const $navButton = event.target.closest('.nav__item__button');
        if (!$navButton) return;

        const $item = $navButton.closest('.nav__item');
        if (!$item) return;

        // Handle hover behavior
        if (this.clickOpenedSubnav) {
            // If there's a click-opened subnav, close it when hovering over a different item
            if ($item !== this.clickOpenedSubnav) {
                this.closeSubnav();
                this.clickOpenedSubnav = null;
                // Only open subnav if this item has one
                this.openSubnav($item);
            }
        } else {
            // If hovering over a different item, close current and open new one (if it has subnav)
            if ($item !== this.$openSubnavItem) {
                this.closeSubnav();
                this.openSubnav($item);
            }
        }
    }

    onSubnavMouseLeave() {
        if (this.isMobile) return;
        if (this.clickOpenedSubnav) return;
        this.closeSubnav();
    }

    // handle clicks outside the navigation
    onDocumentClick(event) {
        const clickInsideSubnav = event.target.closest('[data-js-subnav]');
        const clickOnLevel1Button = event.target.closest('[data-js-level-1-button]');

        if (this.$openSubnavItem && !clickInsideSubnav && !clickOnLevel1Button) {
            this.closeSubnav();
            this.clickOpenedSubnav = null;
        }
    }

    // programmatically trigger click to avoid browser setting scroll position to top
    onMenuLabelClick(event) {
        event.preventDefault();

        if (this.searchIsOpen) {
            const customEvent = new Event('menulabelclicked');
            window.dispatchEvent(customEvent);
        }

        this.isMenuOpen = !this.isMenuOpen;
        document.documentElement.classList.toggle('nav-menu-open', this.isMenuOpen);
        this.$menuLabel.setAttribute('aria-expanded', this.isMenuOpen.toString());
    }

    openSubnav($item) {
        if (!$item) return;

        this.closeSubnav(this.$openSubnavItem); // close previous

        this.$openSubnavItem = $item;
        this.$openSubnavButton = $item.querySelector('[data-js-level-1-button]');
        this.$openSubnav = $item.querySelector('[data-js-subnav]');

        if (!this.$openSubnavButton || !this.$openSubnav) return;

        this.$openSubnavItem.classList.add('is-subnav-open');
        this.$openSubnavButton.setAttribute('aria-expanded', 'true');

        if (this.$navContainer) {
            this.$navContainer.addEventListener('mouseleave', this.onSubnavMouseLeave);
        }

        if (this.isDesktop) {
            document.documentElement.classList.add('nav-subnav-open');
        }
    }

    closeSubnav() {
        if (!this.$openSubnavItem) return;

        if (this.$openSubnav) {
            this.$openSubnav = null;
        }

        this.$openSubnavItem.classList.remove('is-subnav-open');
        if (this.$openSubnavButton) {
            this.$openSubnavButton.setAttribute('aria-expanded', 'false');
        }
        this.$openSubnavItem = null;
        this.$openSubnavButton = null;

        document.documentElement.classList.remove('nav-subnav-open');

        if (this.$navContainer) {
            this.$navContainer.removeEventListener('mouseleave', this.onSubnavMouseLeave);
        }
    }

    handleNestedDropdown($nestedButton) {
        const $nestedList = $nestedButton.nextElementSibling;
        const isExpanded = $nestedButton.getAttribute('aria-expanded') === 'true';

        // Close all other nested dropdowns in the same subnav
        const $subnav = $nestedButton.closest('.nav__subnav');
        const $otherNestedButtons = $subnav.querySelectorAll('[data-js-nested-button]');
        $otherNestedButtons.forEach((button) => {
            if (button !== $nestedButton) {
                button.setAttribute('aria-expanded', 'false');
                button.nextElementSibling.setAttribute('aria-hidden', 'true');
            }
        });

        // Toggle current nested dropdown
        if (isExpanded) {
            $nestedButton.setAttribute('aria-expanded', 'false');
            $nestedList.setAttribute('aria-hidden', 'true');
        } else {
            $nestedButton.setAttribute('aria-expanded', 'true');
            $nestedList.setAttribute('aria-hidden', 'false');
        }
    }

    unmount() {
        this.$element.removeEventListener('keydown', this);
        this.$element.removeEventListener('click', this);
        this.$element.removeEventListener('mouseover', this);
        document.removeEventListener('click', this.onDocumentClickBound);
        window.removeEventListener('scroll', this.onScroll);
        if (this.$navContainer) {
            this.$navContainer.removeEventListener('mouseleave', this.onSubnavMouseLeave);
        }
    }
}

registerModule('Nav', Nav);
